import React, { useState, useEffect, useMemo } from "react";
import { Button, Table, Spinner, Card, Row, Col, Form, Badge, Alert } from "react-bootstrap";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

// Axios instance pointing to base API
const api = axios.create({ baseURL: API_WEB_URLS.BASE });
const AUTH_USER_ID = 0;
const AUTH_TOKEN   = "token";

// Helper to safely extract list from API response regardless of casing
const extractDataList = (res) => {
  if (!res) return [];
  const raw = res.data;
  if (Array.isArray(raw)) return raw.map(normalizeRow);
  if (raw?.data?.dataList && Array.isArray(raw.data.dataList)) return raw.data.dataList.map(normalizeRow);
  if (raw?.Data?.DataList && Array.isArray(raw.Data.DataList)) return raw.Data.DataList.map(normalizeRow);
  if (raw?.dataList && Array.isArray(raw.dataList)) return raw.dataList.map(normalizeRow);
  if (raw?.DataList && Array.isArray(raw.DataList)) return raw.DataList.map(normalizeRow);
  if (raw?.data && Array.isArray(raw.data)) return raw.data.map(normalizeRow);
  if (raw?.Data && Array.isArray(raw.Data)) return raw.Data.map(normalizeRow);
  return [];
};

// Normalizes row keys so both PascalCase and camelCase properties work everywhere
const normalizeRow = (row) => {
  if (!row || typeof row !== "object") return row;
  const n = { ...row };
  for (const [k, v] of Object.entries(row)) {
    const lower = k.charAt(0).toLowerCase() + k.slice(1);
    const upper = k.charAt(0).toUpperCase() + k.slice(1);
    if (!(lower in n)) n[lower] = v;
    if (!(upper in n)) n[upper] = v;
  }
  return n;
};

const BalanceSheet = () => {
  const [balanceData,     setBalanceData]     = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [asOnDate,        setAsOnDate]        = useState(new Date().toISOString().split("T")[0]);
  const [searchTerm,      setSearchTerm]      = useState("");
  const [viewMode,        setViewMode]        = useState("T-Shape"); // "T-Shape" | "Vertical"
  const [expandedGroups,  setExpandedGroups]  = useState({});
  const [error,           setError]           = useState("");

  const navigate = useNavigate();

  // ─── Fetch Balance Sheet with Smart Multi-level Fallbacks ───────────────────
  const fetchBalanceSheet = async (date) => {
    setLoading(true);
    setError("");
    const targetDate = typeof date === "string" && date.trim() ? date.trim() : asOnDate;

    try {
      // 1. Primary: Dedicated BalanceSheet endpoint
      const res = await api.get(
        `${API_WEB_URLS.BALANCE_SHEET_SUMMARY}/${AUTH_USER_ID}/${AUTH_TOKEN}/Summary`,
        { params: { asOnDate: targetDate } }
      );
      const rows = extractDataList(res);
      if (rows && rows.length > 0) {
        setBalanceData(rows);
        setLoading(false);
        return;
      }
    } catch (err1) {
      console.warn("Primary BalanceSheet endpoint unavailable, attempting LedgerReport fallback:", err1?.message || err1);
    }

    try {
      // 2. Secondary Fallback: LedgerReport List with toDate
      const resFallback = await api.get(
        `${API_WEB_URLS.LEDGER_REPORT_LIST}/${AUTH_USER_ID}/${AUTH_TOKEN}/List`,
        { params: { toDate: targetDate } }
      );
      const rowsFallback = extractDataList(resFallback);
      if (rowsFallback && rowsFallback.length > 0) {
        setBalanceData(rowsFallback);
        setLoading(false);
        return;
      }
    } catch (err2) {
      console.warn("Secondary LedgerReport fallback unavailable, attempting Master Ledgers:", err2?.message || err2);
    }

    try {
      // 3. Tertiary Fallback: Master Ledger Listing
      const resMaster = await api.get(
        `${API_WEB_URLS.MASTER}/${AUTH_USER_ID}/${AUTH_TOKEN}/LedgerMaster/Id/0`
      );
      const rowsMaster = extractDataList(resMaster);
      if (rowsMaster && rowsMaster.length > 0) {
        // Map raw ledger master data to balance sheet schema
        const mapped = rowsMaster
          .map((lm) => {
            const lid = Number(lm.Id || lm.id || 0);
            const lgid = Number(lm.F_LedgerGroupMaster || 0);
            let cat = lm.Category || lm.category || "";
            if (lid === 39 || lid === 35 || lid === 18 || [3, 14, 15, 16].includes(lgid)) cat = "Expenses";
            else if (lid === 31 || lid === 33) cat = "Income";
            else if (lgid === 10 || lid === 34) cat = "Liabilities";
            else if (lid === 32 || [1, 2].includes(lgid)) cat = "Assets";
            else if (!cat) cat = "Assets";

            return {
              LedgerId: lid,
              LedgerName: lm.Name || lm.name || lm.LedgerName || "Account",
              LedgerCode: lm.Code || lm.code || lm.LedgerCode || "",
              LedgerGroupName: lm.GroupName || lm.groupName || (lgid === 10 ? "Driver Wallets" : "General Accounts"),
              GroupCategory: cat,
              ClosingBalance: Math.abs(Number(lm.OpeningBalance || lm.Balance || 0)),
              ClosingBalanceType: lm.OpeningBalanceType || (Number(lm.OpeningBalance || 0) < 0 ? "Cr" : "Dr"),
            };
          })
          .filter((lm) => lm.ClosingBalance > 0);
        setBalanceData(mapped);
        setLoading(false);
        return;
      }
    } catch (err3) {
      console.error("All Balance Sheet fetch attempts failed:", err3);
    }

    setBalanceData([]);
    setError("No balance sheet data found or API is currently offline. You can select a different date or retry.");
    setLoading(false);
  };

  // Auto-fetch on mount AND whenever asOnDate changes
  useEffect(() => {
    fetchBalanceSheet(asOnDate);
  }, [asOnDate]);

  // Toggle accordion group expansion
  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  // Group and categorize balances
  const categorizedData = useMemo(() => {
    const assetsGroups = {};
    const liabilitiesGroups = {};
    let totalAssets = 0;
    let totalLiabilities = 0;
    let totalIncome = 0;
    let totalExpenses = 0;
    let currentFYIncome = 0;
    let currentFYExpenses = 0;
    let prevFYIncome = 0;
    let prevFYExpenses = 0;

    // Determine Financial Year based on asOnDate (Indian FY: April 1 to March 31)
    const targetDate = asOnDate ? new Date(asOnDate) : new Date();
    const curYear = isNaN(targetDate.getFullYear()) ? new Date().getFullYear() : targetDate.getFullYear();
    const curMonth = isNaN(targetDate.getMonth()) ? new Date().getMonth() + 1 : targetDate.getMonth() + 1;
    const fyStartYear = curMonth >= 4 ? curYear : curYear - 1;
    const fyLabel = `FY ${fyStartYear}-${String(fyStartYear + 1).slice(2)}`;

    const filtered = balanceData.filter((item) => {
      const bal = Number(item.ClosingBalance || 0);
      const dr = Number(item.TotalDebit || 0);
      const cr = Number(item.TotalCredit || 0);
      const op = Number(item.OpeningBalance || 0);

      // Filter out accounts with 0 balance completely
      if (bal === 0 && dr === 0 && cr === 0 && op === 0) return false;
      if (bal === 0 && (item.GroupCategory === "Liabilities" || item.GroupCategory === "Assets")) return false;

      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        item.LedgerName?.toLowerCase().includes(term) ||
        item.LedgerGroupName?.toLowerCase().includes(term) ||
        item.LedgerCode?.toLowerCase().includes(term)
      );
    });

    filtered.forEach((item) => {
      const lid = Number(item.LedgerId || item.Id || 0);
      const lgid = Number(item.F_LedgerGroupMaster || 0);
      let cat = (item.GroupCategory || item.category || "").trim();

      // Ensure proper expense and income recognition
      if (lid === 39 || lid === 35 || lid === 18 || [3, 14, 15, 16].includes(lgid)) {
        cat = "Expenses";
      } else if (lid === 31 || lid === 33) {
        cat = "Income";
      } else if (lid === 32 || [1, 2].includes(lgid)) {
        cat = "Assets";
      } else if (lgid === 10 || lid === 34) {
        cat = "Liabilities";
      }

      const groupName = item.LedgerGroupName || (cat === "Income" ? "Direct / Operating Income" : cat === "Expenses" ? "Direct & Indirect Costs" : "General Accounts");
      const balance = Number(item.ClosingBalance || 0);
      const curDr = Number(item.CurrentFYDebit ?? item.TotalDebit ?? 0);
      const curCr = Number(item.CurrentFYCredit ?? item.TotalCredit ?? 0);
      const prevDr = Number(item.PreviousFYDebit ?? 0);
      const prevCr = Number(item.PreviousFYCredit ?? 0);

      if (cat === "Income") {
        totalIncome += balance;
        if (item.CurrentFYCredit !== undefined && item.CurrentFYCredit !== null) {
          currentFYIncome += curCr;
          prevFYIncome += prevCr;
        } else {
          currentFYIncome += balance;
        }
      } else if (cat === "Expenses") {
        totalExpenses += balance;
        if (item.CurrentFYDebit !== undefined && item.CurrentFYDebit !== null) {
          currentFYExpenses += curDr;
          prevFYExpenses += prevDr;
        } else {
          currentFYExpenses += balance;
        }
      } else if (cat === "Liabilities" || cat === "Equity") {
        if (!liabilitiesGroups[groupName]) {
          liabilitiesGroups[groupName] = { name: groupName, total: 0, items: [] };
        }
        liabilitiesGroups[groupName].items.push(item);
        liabilitiesGroups[groupName].total += balance;
        totalLiabilities += balance;
      } else {
        // Default to Assets
        if (!assetsGroups[groupName]) {
          assetsGroups[groupName] = { name: groupName, total: 0, items: [] };
        }
        assetsGroups[groupName].items.push(item);
        assetsGroups[groupName].total += balance;
        totalAssets += balance;
      }
    });

    // ─── 1. Previous Financial Years Net P&L (Retained Earnings) ─────────────
    const prevFYNetProfit = prevFYIncome - prevFYExpenses;

    // ─── 2. Current Financial Year Net P&L (Current Period) ───────────────────
    const effectiveCurrRevenue = (currentFYIncome > 0 || currentFYExpenses > 0) ? currentFYIncome : totalIncome;
    const effectiveCurrExpenses = (currentFYIncome > 0 || currentFYExpenses > 0) ? currentFYExpenses : totalExpenses;
    const currentFYNetProfit = effectiveCurrRevenue - effectiveCurrExpenses;

    // Overall Net Profit
    const netProfit = prevFYNetProfit + currentFYNetProfit;

    // ─── Add 1. Previous Years P&L Account into Liabilities & Equity ─────────
    if (prevFYNetProfit !== 0 || prevFYIncome > 0 || prevFYExpenses > 0) {
      const prevProfitGroupName = "Profit & Loss (Previous Financial Years / Retained Earnings)";
      liabilitiesGroups[prevProfitGroupName] = {
        name: prevProfitGroupName,
        total: prevFYNetProfit,
        items: [
          {
            LedgerId: 0,
            LedgerName: prevFYNetProfit >= 0 ? "Accumulated Profit (Opening Balance)" : "Accumulated Loss (Opening Balance)",
            LedgerCode: "P&L-PREV",
            ClosingBalance: Math.abs(prevFYNetProfit),
            ClosingBalanceType: prevFYNetProfit >= 0 ? "Cr" : "Dr",
            isCalculated: true,
            details: `Prior to 01-Apr-${fyStartYear} (Revenue: ₹${prevFYIncome.toFixed(2)} | Cost: ₹${prevFYExpenses.toFixed(2)})`,
          },
        ],
      };
      totalLiabilities += prevFYNetProfit;
    }

    // ─── Add 2. Current Financial Year P&L into Liabilities & Equity ──────────
    if (currentFYNetProfit !== 0 || effectiveCurrRevenue > 0 || effectiveCurrExpenses > 0) {
      const currProfitGroupName = `Current Year Profit & Loss (${fyLabel})`;
      liabilitiesGroups[currProfitGroupName] = {
        name: currProfitGroupName,
        total: currentFYNetProfit,
        items: [
          {
            LedgerId: 0,
            LedgerName: currentFYNetProfit >= 0 ? `Net Profit for Current Period (${fyLabel})` : `Net Loss for Current Period (${fyLabel})`,
            LedgerCode: "P&L-CURR",
            ClosingBalance: Math.abs(currentFYNetProfit),
            ClosingBalanceType: currentFYNetProfit >= 0 ? "Cr" : "Dr",
            isCalculated: true,
            details: `Current FY Revenue: ₹${effectiveCurrRevenue.toFixed(2)} | Current FY Expenses: ₹${effectiveCurrExpenses.toFixed(2)}`,
          },
        ],
      };
      totalLiabilities += currentFYNetProfit;
    }

    return {
      assetsGroups: Object.values(assetsGroups),
      liabilitiesGroups: Object.values(liabilitiesGroups),
      totalAssets,
      totalLiabilities,
      totalIncome: effectiveCurrRevenue,
      totalExpenses: effectiveCurrExpenses,
      currentFYNetProfit,
      prevFYNetProfit,
      netProfit,
      fyLabel,
      isBalanced: Math.abs(totalAssets - totalLiabilities) < 1,
      difference: Math.abs(totalAssets - totalLiabilities),
    };
  }, [balanceData, searchTerm, asOnDate]);

  // Export CSV
  const handleExportCSV = () => {
    let csvContent = `"Balance Sheet","Kirayago Logistics"\n`;
    csvContent += `"As On Date","${asOnDate}"\n\n`;

    csvContent += `"LIABILITIES & EQUITY","AMOUNT (₹)","ASSETS","AMOUNT (₹)"\n`;

    const liabItems = [];
    categorizedData.liabilitiesGroups.forEach((g) => {
      liabItems.push({ name: `[GROUP] ${g.name}`, amount: g.total.toFixed(2), isGroup: true });
      g.items.forEach((item) => {
        liabItems.push({ name: `  ${item.LedgerName}`, amount: item.ClosingBalance.toFixed(2), isGroup: false });
      });
    });

    const assetItems = [];
    categorizedData.assetsGroups.forEach((g) => {
      assetItems.push({ name: `[GROUP] ${g.name}`, amount: g.total.toFixed(2), isGroup: true });
      g.items.forEach((item) => {
        assetItems.push({ name: `  ${item.LedgerName}`, amount: item.ClosingBalance.toFixed(2), isGroup: false });
      });
    });

    const maxRows = Math.max(liabItems.length, assetItems.length);
    for (let i = 0; i < maxRows; i++) {
      const l = liabItems[i] || { name: "", amount: "" };
      const a = assetItems[i] || { name: "", amount: "" };
      csvContent += `"${l.name}","${l.amount}","${a.name}","${a.amount}"\n`;
    }

    csvContent += `"TOTAL LIABILITIES & EQUITY","${categorizedData.totalLiabilities.toFixed(2)}","TOTAL ASSETS","${categorizedData.totalAssets.toFixed(2)}"\n`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Balance_Sheet_${asOnDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print PDF
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Header Title */}
        <Row className="mb-3 align-items-center">
          <Col md={6}>
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  backgroundColor: "#EFF6FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 12,
                }}
              >
                <i className="bx bx-bar-chart-alt-2 text-primary" style={{ fontSize: 24 }}></i>
              </div>
              <div>
                <h4 className="mb-0 font-size-18 text-dark font-weight-bold">Balance Sheet</h4>
                <p className="text-muted mb-0 font-size-12">
                  Company financial health, captain wallet liabilities, bank assets & retained earnings
                </p>
              </div>
            </div>
          </Col>
          <Col md={6} className="text-md-end mt-2 mt-md-0">
            <Button variant="outline-secondary" size="sm" className="me-2" onClick={handlePrint}>
              <i className="bx bx-printer me-1"></i> Print
            </Button>
            <Button variant="outline-success" size="sm" className="me-2" onClick={handleExportCSV}>
              <i className="bx bx-download me-1"></i> Export Excel
            </Button>
            <Button variant="primary" size="sm" onClick={() => fetchBalanceSheet(asOnDate)}>
              <i className="bx bx-refresh me-1"></i> Refresh
            </Button>
          </Col>
        </Row>

        {error && (
          <Alert variant="warning" className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <i className="bx bx-info-circle me-2 font-size-16 align-middle"></i>
              <span>{error}</span>
            </div>
            <Button size="sm" variant="outline-dark" onClick={() => fetchBalanceSheet(asOnDate)}>
              Try Again
            </Button>
          </Alert>
        )}

        {/* Top Summary Cards */}
        <Row className="mb-3">
          <Col xl={3} md={6}>
            <Card className="mini-stats-wid shadow-sm border-0">
              <Card.Body>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-1 font-size-12">Total Assets</p>
                    <h5 className="mb-0 text-success fw-bold">
                      ₹{categorizedData.totalAssets.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </h5>
                  </div>
                  <div className="mini-stat-icon avatar-sm rounded-circle bg-success align-self-center">
                    <span className="avatar-title bg-success rounded-circle">
                      <i className="bx bx-wallet font-size-22"></i>
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="mini-stats-wid shadow-sm border-0">
              <Card.Body>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-1 font-size-12">Total Liabilities & Equity</p>
                    <h5 className="mb-0 text-danger fw-bold">
                      ₹{categorizedData.totalLiabilities.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </h5>
                  </div>
                  <div className="mini-stat-icon avatar-sm rounded-circle bg-danger align-self-center">
                    <span className="avatar-title bg-danger rounded-circle">
                      <i className="bx bx-credit-card font-size-22"></i>
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="mini-stats-wid shadow-sm border-0">
              <Card.Body>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-1 font-size-12">Net Profit ({categorizedData.fyLabel || "Current FY"})</p>
                    <h5 className={`mb-0 fw-bold ${categorizedData.currentFYNetProfit >= 0 ? "text-primary" : "text-danger"}`}>
                      ₹{categorizedData.currentFYNetProfit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </h5>
                    <small className="text-muted font-size-11">
                      Rev: ₹{categorizedData.totalIncome.toFixed(2)} | Exp: ₹{categorizedData.totalExpenses.toFixed(2)}
                    </small>
                  </div>
                  <div className="mini-stat-icon avatar-sm rounded-circle bg-primary align-self-center">
                    <span className="avatar-title bg-primary rounded-circle">
                      <i className="bx bx-trending-up font-size-22"></i>
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} md={6}>
            <Card className="mini-stats-wid shadow-sm border-0">
              <Card.Body>
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-1 font-size-12">Balancing Status</p>
                    <h5 className="mb-0 fw-bold">
                      {categorizedData.isBalanced ? (
                        <span className="text-success"><i className="bx bx-check-circle me-1"></i> Balanced</span>
                      ) : (
                        <span className="text-warning">
                          Diff: ₹{categorizedData.difference.toFixed(2)}
                        </span>
                      )}
                    </h5>
                  </div>
                  <div className="mini-stat-icon avatar-sm rounded-circle bg-info align-self-center">
                    <span className="avatar-title bg-info rounded-circle">
                      <i className="bx bx-scale font-size-22"></i>
                    </span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filter Controls Bar */}
        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="py-2">
            <Row className="align-items-center g-2">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="font-size-11 mb-1 text-muted fw-bold">AS ON DATE</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={asOnDate}
                    onChange={(e) => setAsOnDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="font-size-11 mb-1 text-muted fw-bold">SEARCH ACCOUNT / GROUP</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Search ledgers (e.g. Bank, Driver, Commission)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="font-size-11 mb-1 text-muted fw-bold">VIEW FORMAT</Form.Label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${viewMode === "T-Shape" ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setViewMode("T-Shape")}
                    >
                      <i className="bx bx-columns me-1"></i> T-Format
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${viewMode === "Vertical" ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setViewMode("Vertical")}
                    >
                      <i className="bx bx-list-ul me-1"></i> Vertical
                    </button>
                  </div>
                </Form.Group>
              </Col>
              <Col md={2} className="text-end pt-3">
                <Button
                  variant="link"
                  size="sm"
                  className="text-decoration-none p-0"
                  onClick={() => {
                    const allOpen = {};
                    categorizedData.assetsGroups.forEach((g) => (allOpen[g.name] = true));
                    categorizedData.liabilitiesGroups.forEach((g) => (allOpen[g.name] = true));
                    setExpandedGroups(allOpen);
                  }}
                >
                  Expand All
                </Button>
                <span className="mx-2 text-muted">|</span>
                <Button
                  variant="link"
                  size="sm"
                  className="text-decoration-none p-0"
                  onClick={() => setExpandedGroups({})}
                >
                  Collapse
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Calculating financial statements & ledger balances...</p>
          </div>
        ) : (
          <>
            {/* T-Format Double Column Balance Sheet */}
            {viewMode === "T-Shape" ? (
              <Row>
                {/* Left Side: Liabilities & Equity */}
                <Col lg={6} className="pe-lg-1">
                  <Card className="shadow-sm border-0 h-100">
                    <div className="card-header bg-danger text-white py-2 d-flex justify-content-between align-items-center">
                      <h6 className="mb-0 fw-bold text-white font-size-14">
                        <i className="bx bx-credit-card me-1"></i> LIABILITIES & CAPITAL
                      </h6>
                      <span className="badge bg-white text-danger fw-bold">
                        ₹{categorizedData.totalLiabilities.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <Card.Body className="p-0">
                      <Table hover responsive className="mb-0 align-middle">
                        <thead className="table-light font-size-11 text-uppercase text-muted">
                          <tr>
                            <th>Particulars / Ledger Group</th>
                            <th className="text-end" style={{ width: 140 }}>Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categorizedData.liabilitiesGroups.length === 0 ? (
                            <tr>
                              <td colSpan={2} className="text-center text-muted py-4">No liabilities found</td>
                            </tr>
                          ) : (
                            categorizedData.liabilitiesGroups.map((group) => {
                              const isExpanded = !!expandedGroups[group.name];
                              return (
                                <React.Fragment key={group.name}>
                                  {/* Group Header Row */}
                                  <tr
                                    style={{ backgroundColor: "#F8FAFC", cursor: "pointer" }}
                                    onClick={() => toggleGroup(group.name)}
                                  >
                                    <td className="fw-bold text-dark font-size-13 py-2">
                                      <i className={`bx ${isExpanded ? "bx-chevron-down" : "bx-chevron-right"} me-1 text-primary`}></i>
                                      {group.name}
                                      <span className="badge bg-light text-muted ms-2 fw-normal font-size-10">
                                        {group.items.length} accounts
                                      </span>
                                    </td>
                                    <td className="text-end fw-bold text-dark font-size-13 py-2">
                                      ₹{group.total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                    </td>
                                  </tr>

                                  {/* Sub-ledgers (Expandable) */}
                                  {isExpanded &&
                                    group.items.map((item, idx) => (
                                      <tr key={idx} style={{ backgroundColor: "#FFFFFF" }}>
                                        <td className="ps-4 py-2">
                                          <div className="d-flex align-items-center">
                                            <i className="bx bx-right-arrow-alt text-muted me-1 font-size-12"></i>
                                            <span
                                              className="text-primary font-size-12 fw-medium"
                                              style={{ cursor: item.LedgerId ? "pointer" : "default" }}
                                              onClick={() => {
                                                if (item.LedgerId) {
                                                  navigate(`/LedgerReport?ledgerId=${item.LedgerId}`);
                                                }
                                              }}
                                              title={item.LedgerId ? "Click to view Ledger Statement" : ""}
                                            >
                                              {item.LedgerName}
                                            </span>
                                            {item.LedgerCode && (
                                              <span className="text-muted font-size-10 ms-1">({item.LedgerCode})</span>
                                            )}
                                          </div>
                                          {item.details && (
                                            <small className="text-muted d-block ps-3 font-size-10">{item.details}</small>
                                          )}
                                        </td>
                                        <td className="text-end font-size-12 text-muted py-2">
                                          ₹{item.ClosingBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                        </td>
                                      </tr>
                                    ))}
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                        <tfoot className="table-light">
                          <tr className="border-top-2">
                            <th className="fw-bold text-dark font-size-13 py-3">TOTAL LIABILITIES & EQUITY</th>
                            <th className="text-end fw-bold text-danger font-size-14 py-3">
                              ₹{categorizedData.totalLiabilities.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </th>
                          </tr>
                        </tfoot>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Right Side: Assets */}
                <Col lg={6} className="ps-lg-1 mt-3 mt-lg-0">
                  <Card className="shadow-sm border-0 h-100">
                    <div className="card-header bg-success text-white py-2 d-flex justify-content-between align-items-center">
                      <h6 className="mb-0 fw-bold text-white font-size-14">
                        <i className="bx bx-wallet me-1"></i> ASSETS & PROPERTY
                      </h6>
                      <span className="badge bg-white text-success fw-bold">
                        ₹{categorizedData.totalAssets.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <Card.Body className="p-0">
                      <Table hover responsive className="mb-0 align-middle">
                        <thead className="table-light font-size-11 text-uppercase text-muted">
                          <tr>
                            <th>Particulars / Ledger Group</th>
                            <th className="text-end" style={{ width: 140 }}>Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categorizedData.assetsGroups.length === 0 ? (
                            <tr>
                              <td colSpan={2} className="text-center text-muted py-4">No assets found</td>
                            </tr>
                          ) : (
                            categorizedData.assetsGroups.map((group) => {
                              const isExpanded = !!expandedGroups[group.name];
                              return (
                                <React.Fragment key={group.name}>
                                  {/* Group Header Row */}
                                  <tr
                                    style={{ backgroundColor: "#F8FAFC", cursor: "pointer" }}
                                    onClick={() => toggleGroup(group.name)}
                                  >
                                    <td className="fw-bold text-dark font-size-13 py-2">
                                      <i className={`bx ${isExpanded ? "bx-chevron-down" : "bx-chevron-right"} me-1 text-primary`}></i>
                                      {group.name}
                                      <span className="badge bg-light text-muted ms-2 fw-normal font-size-10">
                                        {group.items.length} accounts
                                      </span>
                                    </td>
                                    <td className="text-end fw-bold text-dark font-size-13 py-2">
                                      ₹{group.total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                    </td>
                                  </tr>

                                  {/* Sub-ledgers (Expandable) */}
                                  {isExpanded &&
                                    group.items.map((item, idx) => (
                                      <tr key={idx} style={{ backgroundColor: "#FFFFFF" }}>
                                        <td className="ps-4 py-2">
                                          <div className="d-flex align-items-center">
                                            <i className="bx bx-right-arrow-alt text-muted me-1 font-size-12"></i>
                                            <span
                                              className="text-primary font-size-12 fw-medium"
                                              style={{ cursor: item.LedgerId ? "pointer" : "default" }}
                                              onClick={() => {
                                                if (item.LedgerId) {
                                                  navigate(`/LedgerReport?ledgerId=${item.LedgerId}`);
                                                }
                                              }}
                                              title={item.LedgerId ? "Click to view Ledger Statement" : ""}
                                            >
                                              {item.LedgerName}
                                            </span>
                                            {item.LedgerCode && (
                                              <span className="text-muted font-size-10 ms-1">({item.LedgerCode})</span>
                                            )}
                                          </div>
                                        </td>
                                        <td className="text-end font-size-12 text-muted py-2">
                                          ₹{item.ClosingBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                        </td>
                                      </tr>
                                    ))}
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                        <tfoot className="table-light">
                          <tr className="border-top-2">
                            <th className="fw-bold text-dark font-size-13 py-3">TOTAL ASSETS</th>
                            <th className="text-end fw-bold text-success font-size-14 py-3">
                              ₹{categorizedData.totalAssets.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </th>
                          </tr>
                        </tfoot>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            ) : (
              /* Vertical View */
              <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0 align-middle">
                    <thead className="table-dark">
                      <tr>
                        <th>Account Schedule / Group</th>
                        <th>Category</th>
                        <th className="text-end">Total Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Liabilities Section Header */}
                      <tr className="table-danger">
                        <td colSpan={3} className="fw-bold text-danger font-size-14">
                          <i className="bx bx-credit-card me-1"></i> I. LIABILITIES & CAPITAL
                        </td>
                      </tr>
                      {categorizedData.liabilitiesGroups.map((g) => (
                        <tr key={g.name}>
                          <td className="ps-4 fw-medium text-dark">{g.name} ({g.items.length} accounts)</td>
                          <td><Badge bg="danger">Liability / Equity</Badge></td>
                          <td className="text-end fw-bold">₹{g.total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                      <tr className="bg-light fw-bold">
                        <td colSpan={2} className="text-end text-dark">TOTAL LIABILITIES & EQUITY:</td>
                        <td className="text-end text-danger fw-bold font-size-14">
                          ₹{categorizedData.totalLiabilities.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                      </tr>

                      {/* Assets Section Header */}
                      <tr className="table-success">
                        <td colSpan={3} className="fw-bold text-success font-size-14">
                          <i className="bx bx-wallet me-1"></i> II. ASSETS & PROPERTIES
                        </td>
                      </tr>
                      {categorizedData.assetsGroups.map((g) => (
                        <tr key={g.name}>
                          <td className="ps-4 fw-medium text-dark">{g.name} ({g.items.length} accounts)</td>
                          <td><Badge bg="success">Asset</Badge></td>
                          <td className="text-end fw-bold">₹{g.total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                      <tr className="bg-light fw-bold">
                        <td colSpan={2} className="text-end text-dark">TOTAL ASSETS:</td>
                        <td className="text-end text-success fw-bold font-size-14">
                          ₹{categorizedData.totalAssets.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BalanceSheet;
